import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Calendar, Clock, Plus, Edit, Trash2, Users, Copy, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { ProjectCombobox } from '@/components/dashboard/ProjectCombobox';
import { MobileScheduleBuilder } from '@/components/dashboard/MobileScheduleBuilder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ScheduleManagement({ projects, employees, user, renderWeeklyCalendar, renderMonthlyCalendar, openBuilderToken }) {
  const [schedules, setSchedules] = useState([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showEditScheduleDialog, setShowEditScheduleDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [showScheduleDetailsDialog, setShowScheduleDetailsDialog] = useState(false);
  const [selectedScheduleDetails, setSelectedScheduleDetails] = useState(null);
  const [scheduleView, setScheduleView] = useState('list');
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    return new Date(today.setDate(diff));
  });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [scheduleConflicts, setScheduleConflicts] = useState([]);
  const [copyTargetDate, setCopyTargetDate] = useState('');
  const [copyingSchedule, setCopyingSchedule] = useState(false);
  const [copyConflictData, setCopyConflictData] = useState(null);
  const [splitTimes, setSplitTimes] = useState({});
  const [showBuilder, setShowBuilder] = useState(false);

  // Allow a parent (e.g. mobile tile grid) to open the Quick Builder by
  // bumping `openBuilderToken`. Any non-initial change triggers the dialog.
  useEffect(() => {
    if (openBuilderToken && openBuilderToken > 0) setShowBuilder(true);
  }, [openBuilderToken]);

  const handleApplyTimeSplit = async (conflicts, isFromCopy = false) => {
    const token = localStorage.getItem('token');
    setLoading(true);
    let allSuccess = true;
    
    try {
      // 1. Update existing schedules with new end times
      for (const conflict of conflicts) {
        const times = splitTimes[conflict.user_id];
        if (!times || !conflict.existing_schedule_id) continue;
        
        const updateRes = await fetch(`${API_URL}/api/schedules/${conflict.existing_schedule_id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            start_time: times.existing_start,
            end_time: times.existing_end
          })
        });
        if (!updateRes.ok) {
          toast.error(`Failed to update ${conflict.user_name}'s existing schedule`);
          allSuccess = false;
        }
      }
      
      // 2. Create the new schedule with split times (force_create to skip conflict check)
      const newStart = Object.values(splitTimes)[0]?.new_start || scheduleForm.start_time;
      const newEnd = Object.values(splitTimes)[0]?.new_end || scheduleForm.end_time;
      
      if (isFromCopy && copyConflictData) {
        const copyData = copyConflictData;
        const createRes = await fetch(`${API_URL}/api/schedules`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            project_id: copyData.schedule.project_id,
            date: copyData.targetDate,
            team_members: copyData.schedule.team_members || [],
            start_time: newStart,
            end_time: newEnd,
            notes: copyData.schedule.notes,
            status: 'scheduled',
            force_create: true
          })
        });
        if (createRes.ok) {
          toast.success('Schedule copied with time split applied');
        } else { allSuccess = false; }
        setCopyConflictData(null);
        setCopyTargetDate('');
        setShowScheduleDetailsDialog(false);
      } else {
        const createRes = await fetch(`${API_URL}/api/schedules`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...scheduleForm,
            start_time: newStart,
            end_time: newEnd,
            force_create: true
          })
        });
        if (createRes.ok) {
          toast.success('Schedule created with time split applied');
          setShowScheduleDialog(false);
          setScheduleForm({ project_id: '', date: getNextWorkingDay(), team_members: [], is_recurring: false, recurring_pattern: 'daily', recurring_end_date: '', start_time: '07:00', end_time: '15:00', notes: '', status: 'scheduled' });
        } else { allSuccess = false; }
      }
      
      setScheduleConflicts([]);
      setSplitTimes({});
      fetchSchedules();
      
      if (!allSuccess) toast.warning('Some updates may have failed — please verify schedules');
    } catch {
      toast.error('Connection error during time split');
    }
    setLoading(false);
  };

  const handleCopySchedule = async (schedule, targetDate, forceCreate = false, source = 'copy_button') => {
    if (!targetDate) { toast.error('Please select a target date'); return; }
    if (schedule.date === targetDate) { toast.error('Cannot copy to the same date'); return; }
    setCopyingSchedule(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: schedule.project_id,
          date: targetDate,
          team_members: schedule.team_members || [],
          start_time: schedule.start_time,
          end_time: schedule.end_time,
          notes: schedule.notes,
          status: 'scheduled',
          force_create: forceCreate,
          // Audit-only — backend persists, dashboard never displays.
          source_date: schedule.date,
          source_method: source,
        })
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'conflict' && !forceCreate) {
          setCopyConflictData({ schedule, targetDate, conflicts: result.conflicts });
          // Make sure the details dialog (which hosts the conflict-resolution
          // UI) is open and pinned to the right schedule + target date so the
          // user can act on the conflict — required when copy is initiated
          // by drag-drop, since the dialog isn't already open in that case.
          setSelectedScheduleDetails(schedule);
          setCopyTargetDate(targetDate);
          setShowScheduleDetailsDialog(true);
          toast.warning(`${result.conflicts.length} conflict(s) found — choose how to proceed`);
          setCopyingSchedule(false);
          return;
        }
        toast.success(`Schedule copied to ${formatDateDisplay(targetDate)}`);
        setCopyTargetDate('');
        setCopyConflictData(null);
        setShowScheduleDetailsDialog(false);
        const wEnd = new Date(currentWeekStart);
        wEnd.setDate(currentWeekStart.getDate() + 6);
        fetchSchedules(currentWeekStart.toISOString().split('T')[0], wEnd.toISOString().split('T')[0]);
      } else {
        toast.error('Failed to copy schedule');
      }
    } catch { toast.error('Connection error'); }
    setCopyingSchedule(false);
  };

  const getNextWorkingDay = () => {
    const today = new Date();
    let nextDay = new Date(today);
    nextDay.setDate(nextDay.getDate() + 1);
    while (nextDay.getDay() === 0 || nextDay.getDay() === 6) {
      nextDay.setDate(nextDay.getDate() + 1);
    }
    return nextDay.toISOString().split('T')[0];
  };

  const [scheduleForm, setScheduleForm] = useState({
    project_id: '',
    date: getNextWorkingDay(),
    team_members: [],
    is_recurring: false,
    recurring_pattern: 'daily',
    recurring_end_date: '',
    start_time: '07:00',
    end_time: '15:00',
    notes: '',
    status: 'scheduled'
  });

  const toLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateDisplay = (dateString, options = {}) => {
    if (!dateString) return '';
    const parts = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    if (options.weekday) opts.weekday = 'long';
    return date.toLocaleDateString('en-US', opts);
  };

  const fetchSchedules = useCallback(async (startDate, endDate) => {
    const token = localStorage.getItem('token');
    try {
      const params = new URLSearchParams();
      if (!startDate || !endDate) {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - dayOfWeek);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        startDate = toLocalDateString(weekStart);
        endDate = toLocalDateString(weekEnd);
      }
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      const response = await fetch(`${API_URL}/api/schedules?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSchedules(await response.json());
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleForm)
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'conflict' && Array.isArray(result.conflicts)) {
          const validConflicts = result.conflicts.filter(c => c && typeof c === 'object' && c.user_name && !c.type && !c.loc && !c.msg);
          if (validConflicts.length > 0) {
            setScheduleConflicts(validConflicts);
            // Initialize split times for each conflict
            const newSplitTimes = {};
            validConflicts.forEach(c => {
              const existStart = c.existing_start_time || '07:00';
              const existEnd = c.existing_end_time || '15:00';
              // Pre-fill: existing project keeps first half, new project gets second half
              const midHour = Math.floor((parseInt(existStart) + parseInt(existEnd)) / 2);
              const midTime = `${String(midHour).padStart(2, '0')}:00`;
              newSplitTimes[c.user_id] = {
                existing_start: existStart,
                existing_end: midTime,
                new_start: midTime,
                new_end: existEnd
              };
            });
            setSplitTimes(newSplitTimes);
            toast.warning('Scheduling conflicts detected! Review and resolve.');
            return;
          }
        }
        toast.success(result.message);
        setShowScheduleDialog(false);
        setScheduleForm({ project_id: '', date: getNextWorkingDay(), team_members: [], is_recurring: false, recurring_pattern: 'daily', recurring_end_date: '', start_time: '07:00', end_time: '15:00', notes: '', status: 'scheduled' });
        setScheduleConflicts([]);
        fetchSchedules();
      } else {
        const error = await response.json();
        if (Array.isArray(error.detail)) {
          toast.error(`Validation error: ${error.detail.map(err => err.msg || JSON.stringify(err)).join(', ')}`);
        } else {
          toast.error(error.detail || 'Failed to create schedule');
        }
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleForm({
      project_id: schedule.project_id || '', date: schedule.date || getNextWorkingDay(),
      team_members: schedule.team_members || [], is_recurring: false, recurring_pattern: 'daily', recurring_end_date: '',
      start_time: schedule.start_time || '07:00', end_time: schedule.end_time || '15:00',
      notes: schedule.notes || '', status: schedule.status || 'scheduled'
    });
    setShowEditScheduleDialog(true);
  };

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    if (!editingSchedule) return;
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/schedules/${editingSchedule.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: scheduleForm.date, team_members: scheduleForm.team_members, project_id: scheduleForm.project_id, start_time: scheduleForm.start_time, end_time: scheduleForm.end_time, notes: scheduleForm.notes, status: scheduleForm.status })
      });
      if (response.ok) {
        toast.success('Schedule updated successfully');
        setShowEditScheduleDialog(false);
        setEditingSchedule(null);
        setScheduleForm({ project_id: '', date: getNextWorkingDay(), team_members: [], is_recurring: false, recurring_pattern: 'daily', recurring_end_date: '', start_time: '07:00', end_time: '15:00', notes: '', status: 'scheduled' });
        fetchSchedules();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update schedule');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Schedule deleted');
        fetchSchedules();
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const getSchedulesForDate = (dateStr) => {
    const dateSchedules = schedules.filter(schedule => schedule.date.split('T')[0] === dateStr);
    const groupedByProject = {};
    dateSchedules.forEach(schedule => {
      const key = schedule.project_id || schedule.project_name;
      if (!groupedByProject[key]) {
        groupedByProject[key] = { ...schedule, team_details: [...(schedule.team_details || [])], time_slots: schedule.start_time && schedule.end_time ? [`${schedule.start_time} - ${schedule.end_time}`] : [] };
      } else {
        const existing = groupedByProject[key];
        (schedule.team_details || []).forEach(member => {
          if (!existing.team_details.find(m => m.id === member.id)) existing.team_details.push(member);
        });
        if (schedule.start_time && schedule.end_time) {
          const slot = `${schedule.start_time} - ${schedule.end_time}`;
          if (!existing.time_slots.includes(slot)) existing.time_slots.push(slot);
        }
      }
    });
    return Object.values(groupedByProject);
  };

  const navigateWeek = (direction) => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newWeekStart);
    const weekEnd = new Date(newWeekStart);
    weekEnd.setDate(newWeekStart.getDate() + 6);
    fetchSchedules(newWeekStart.toISOString().split('T')[0], weekEnd.toISOString().split('T')[0]);
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const renderInternalWeeklyCalendar = () => {
    const weekDays = getWeekDays();
    const weekStart = weekDays[0].toLocaleDateString('default', { month: 'short', day: 'numeric' });
    const weekEnd = weekDays[6].toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' });

    const handleDragStart = (e, schedule) => {
      e.dataTransfer.setData('application/json', JSON.stringify(schedule));
      e.target.style.opacity = '0.5';
    };
    const handleDragEnd = (e) => { e.target.style.opacity = '1'; };
    const handleDragOver = (e) => {
      e.preventDefault();
      e.currentTarget.style.backgroundColor = '#e0f2fe';
    };
    const handleDrop = async (e, targetDate) => {
      e.preventDefault();
      e.currentTarget.style.backgroundColor = '';
      try {
        const schedule = JSON.parse(e.dataTransfer.getData('application/json'));
        if (schedule.date === targetDate) return;
        // Reuse the same conflict-aware copy flow as the "Copy" button. If
        // the server reports a conflict, handleCopySchedule will open the
        // schedule details dialog (where the conflict-resolution UI lives).
        await handleCopySchedule(schedule, targetDate, false, 'drag_drop');
      } catch (error) { toast.error('Failed to copy schedule'); }
    };

    return (
      <div className="weekly-calendar">
        <div className="flex justify-between items-center mb-4">
          <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>Previous Week</Button>
          <h3 className="font-medium text-sm">{weekStart} — {weekEnd}</h3>
          <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>Next Week</Button>
        </div>
        <p className="text-xs text-gray-500 text-center mb-2 hidden md:block">Drag and drop schedules to copy them to another day</p>
        <p className="text-xs text-gray-500 text-center mb-2 md:hidden">Tap a schedule to view details or copy it</p>
        <div className="weekly-grid">
          {weekDays.map((day, idx) => {
            const dateStr = toLocalDateString(day);
            const daySchedules = getSchedulesForDate(dateStr);
            const isToday = dateStr === toLocalDateString(new Date());
            return (
              <div key={idx} className={`weekly-day-column ${isToday ? 'today' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                onDrop={(e) => handleDrop(e, dateStr)}>
                <div className="weekly-day-header">
                  <div className="day-name">{dayNames[idx]}</div>
                  <div className="day-date">{day.getDate()} {day.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
                </div>
                <div className="weekly-day-content">
                  {daySchedules.map((schedule, schedIdx) => (
                    <div key={schedIdx} className="weekly-schedule-item cursor-grab hover:bg-blue-100 transition-colors"
                      draggable="true" onDragStart={(e) => handleDragStart(e, schedule)} onDragEnd={handleDragEnd}
                      onClick={() => { setSelectedScheduleDetails(schedule); setShowScheduleDetailsDialog(true); }}
                      title={schedule.team_details?.map(m => m.name).join(', ') || 'No members assigned'}>
                      <div className="schedule-project-name">{schedule.project_name}</div>
                      {schedule.start_time && schedule.end_time && (
                        <div className="schedule-time">{schedule.start_time} - {schedule.end_time}</div>
                      )}
                      <div className="schedule-dot" title={schedule.team_details?.map(m => m.name).join(', ')}>
                        {schedule.team_details?.length || 0} members
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
    const startDate = new Date(newMonth.getFullYear(), newMonth.getMonth(), 1).toISOString().split('T')[0];
    const endDate = new Date(newMonth.getFullYear(), newMonth.getMonth() + 1, 0).toISOString().split('T')[0];
    fetchSchedules(startDate, endDate);
  };

  const renderInternalMonthlyCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks = [];
    let currentWeek = new Array(firstDay).fill(null);
    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(day);
      if (currentWeek.length === 7) { weeks.push(currentWeek); currentWeek = []; }
    }
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) currentWeek.push(null);
      weeks.push(currentWeek);
    }
    return (
      <div className="monthly-calendar">
        <div className="flex justify-between items-center mb-4">
          <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>Prev</Button>
          <h3 className="font-medium">{currentMonth.toLocaleDateString('default', { month: 'long', year: 'numeric' })}</h3>
          <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>Next</Button>
        </div>
        <div className="calendar-grid">
          {dayNames.map(name => <div key={name} className="calendar-day-name">{name}</div>)}
          {weeks.flat().map((day, idx) => {
            if (!day) return <div key={idx} className="calendar-day empty" />;
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const daySchedules = getSchedulesForDate(dateStr);
            const isToday = dateStr === toLocalDateString(new Date());
            return (
              <div key={idx} className={`calendar-day ${isToday ? 'today' : ''} ${daySchedules.length > 0 ? 'has-events' : ''}`}
                onClick={() => { setSelectedDate(dateStr); setScheduleView('list'); fetchSchedules(dateStr, dateStr); }}>
                <div className="day-number">{day}</div>
                {daySchedules.map((schedule, sIdx) => (
                  <div key={sIdx} className="schedule-dot" title={schedule.project_name}>
                    {schedule.project_name?.substring(0, 8)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <CardTitle>Team Schedule</CardTitle>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                    <Button
                      variant={scheduleView === 'today' ? 'default' : 'ghost'}
                      size="sm"
                      className="text-xs sm:text-sm"
                      onClick={() => {
                        setScheduleView('today');
                        const today = toLocalDateString(new Date());
                        fetchSchedules(today, today);
                      }}
                    >
                      Today
                    </Button>
                    <Button
                      variant={scheduleView === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      className="text-xs sm:text-sm"
                      onClick={() => {
                        setScheduleView('list');
                        fetchSchedules();
                      }}
                    >
                      List
                    </Button>
                    <Button
                      variant={scheduleView === 'week' ? 'default' : 'ghost'}
                      size="sm"
                      className="text-xs sm:text-sm"
                      onClick={() => {
                        setScheduleView('week');
                        const weekEnd = new Date(currentWeekStart);
                        weekEnd.setDate(currentWeekStart.getDate() + 6);
                        fetchSchedules(currentWeekStart.toISOString().split('T')[0], weekEnd.toISOString().split('T')[0]);
                      }}
                    >
                      Week
                    </Button>
                    <Button
                      variant={scheduleView === 'calendar' ? 'default' : 'ghost'}
                      size="sm"
                      className="hidden sm:inline-flex text-xs sm:text-sm"
                      onClick={() => {
                        setScheduleView('calendar');
                        const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString().split('T')[0];
                        const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).toISOString().split('T')[0];
                        fetchSchedules(startDate, endDate);
                      }}
                    >
                      Month
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    className="flex-1 sm:flex-none border-blue-500 text-blue-700 hover:bg-blue-50"
                    onClick={() => setShowBuilder(true)}
                    data-testid="open-mob-sched-builder-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Quick Builder</span>
                    <span className="sm:hidden">Builder</span>
                  </Button>
                  <Button 
                    className="flex-1 sm:flex-none"
                    onClick={() => {
                      setShowScheduleDialog(true);
                      fetchSchedules();
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Create Schedule</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-2 sm:p-6">
              {scheduleView === 'today' ? (
                <div className="today-schedule">
                  <div className="mb-3 text-center">
                    <p className="text-sm font-medium text-gray-700">
                      {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  {schedules.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p className="font-medium">No schedules for today</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {schedules.map((schedule) => (
                        <div
                          key={schedule.id}
                          className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => { setSelectedScheduleDetails(schedule); setShowScheduleDetailsDialog(true); }}
                          data-testid={`today-schedule-${schedule.id}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="font-semibold">{schedule.project_name}</div>
                              {schedule.project_address && (
                                <div className="text-sm text-gray-500">{schedule.project_address}</div>
                              )}
                            </div>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              schedule.status === 'completed' ? 'bg-green-100 text-green-800' :
                              schedule.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {schedule.status || 'scheduled'}
                            </span>
                          </div>
                          {schedule.start_time && schedule.end_time && (
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                              <Clock className="w-4 h-4" />
                              <span>{schedule.start_time} - {schedule.end_time}</span>
                            </div>
                          )}
                          {schedule.team_details?.length > 0 && (
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">Team:</span> {schedule.team_details.map(tm => tm.name).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : scheduleView === 'list' ? (
                <div className="schedule-list">
                  <div className="mb-4 flex flex-col sm:flex-row gap-2">
                    <Input
                      type="date"
                      value={selectedDate}
                      className="w-full sm:w-auto"
                      onChange={(e) => {
                        setSelectedDate(e.target.value);
                        // Fetch schedules for selected week
                        const selected = new Date(e.target.value);
                        const dayOfWeek = selected.getDay();
                        const weekStart = new Date(selected);
                        weekStart.setDate(selected.getDate() - dayOfWeek);
                        const weekEnd = new Date(weekStart);
                        weekEnd.setDate(weekStart.getDate() + 6);
                        fetchSchedules(toLocalDateString(weekStart), toLocalDateString(weekEnd));
                      }}
                    />
                    <Button 
                      onClick={() => {
                        // Reset to current week
                        const today = new Date();
                        const dayOfWeek = today.getDay();
                        const weekStart = new Date(today);
                        weekStart.setDate(today.getDate() - dayOfWeek);
                        const weekEnd = new Date(weekStart);
                        weekEnd.setDate(weekStart.getDate() + 6);
                        setSelectedDate(toLocalDateString(today));
                        fetchSchedules(toLocalDateString(weekStart), toLocalDateString(weekEnd));
                      }} 
                      className="w-full sm:w-auto"
                    >
                      This Week
                    </Button>
                  </div>
                  
                  {/* Desktop Table View */}
                  <div className="hidden md:block">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Project</th>
                          <th>Team Members</th>
                          <th>Time</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schedules.map((schedule, index) => (
                          <tr key={schedule.id}>
                            <td>{formatDateDisplay(schedule.date)}</td>
                            <td>
                              <strong>{schedule.project_name}</strong>
                              <br />
                              <span className="text-sm text-gray-500">{schedule.project_address}</span>
                            </td>
                            <td>
                              {schedule.team_details?.map(tm => tm.name).join(', ') || 'None'}
                            </td>
                            <td>
                              {schedule.start_time && schedule.end_time ? (
                                <span className="text-sm">{schedule.start_time} - {schedule.end_time}</span>
                              ) : (
                                <span className="text-gray-400">Not set</span>
                              )}
                            </td>
                            <td>
                              <span className={`px-2 py-1 rounded text-xs ${
                                schedule.status === 'completed' ? 'bg-green-100 text-green-800' :
                                schedule.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                schedule.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {schedule.status || 'scheduled'}
                              </span>
                            </td>
                            <td className="space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditSchedule(schedule)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteSchedule(schedule.id)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-3">
                    {schedules.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No schedules found. Tap "Add" to create one.
                      </div>
                    ) : (
                      schedules.map((schedule) => (
                        <div 
                          key={schedule.id} 
                          className="border rounded-lg p-3 bg-white shadow-sm"
                          data-testid={`schedule-card-${schedule.id}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="font-semibold text-sm">{schedule.project_name}</div>
                              <div className="text-xs text-gray-500">{schedule.project_address}</div>
                            </div>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              schedule.status === 'completed' ? 'bg-green-100 text-green-800' :
                              schedule.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              schedule.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {schedule.status || 'scheduled'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDateDisplay(schedule.date)}
                            </span>
                            {schedule.start_time && schedule.end_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {schedule.start_time} - {schedule.end_time}
                              </span>
                            )}
                          </div>
                          {schedule.team_details?.length > 0 && (
                            <div className="text-xs text-gray-600 mb-2">
                              <span className="font-medium">Team:</span> {schedule.team_details.map(tm => tm.name).join(', ')}
                            </div>
                          )}
                          <div className="flex gap-2 mt-2 pt-2 border-t">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 text-xs"
                              onClick={() => handleEditSchedule(schedule)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 text-xs text-red-600"
                              onClick={() => handleDeleteSchedule(schedule.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : scheduleView === 'calendar' ? (
                renderInternalMonthlyCalendar()
              ) : (
                renderInternalWeeklyCalendar()
              )}
            </CardContent>
          </Card>

          {/* Create Schedule Dialog */}
          <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Team Schedule</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateSchedule} className="space-y-4">
                <div>
                  <Label>Project *</Label>
                  <ProjectCombobox
                    projects={projects}
                    value={scheduleForm.project_id}
                    onChange={(value) => setScheduleForm({...scheduleForm, project_id: value})}
                    testId="create-schedule-project"
                  />
                </div>

                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={scheduleForm.date}
                    onChange={(e) => setScheduleForm({...scheduleForm, date: e.target.value})}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={scheduleForm.start_time}
                      onChange={(e) => setScheduleForm({...scheduleForm, start_time: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={scheduleForm.end_time}
                      onChange={(e) => setScheduleForm({...scheduleForm, end_time: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Team Members *</Label>
                  {/* Search input for mobile-friendly filtering */}
                  <Input
                    type="text"
                    placeholder="Search team members..."
                    className="mb-2"
                    value={scheduleForm.memberSearch || ''}
                    onChange={(e) => setScheduleForm({...scheduleForm, memberSearch: e.target.value})}
                    data-testid="team-member-search"
                  />
                  <div className="border rounded p-3 max-h-48 overflow-y-auto">
                    {[...employees]
                      .filter(emp => {
                        const search = (scheduleForm.memberSearch || '').toLowerCase();
                        if (!search) return true;
                        const name = (emp.user?.name || emp.name || '').toLowerCase();
                        const position = (emp.position || '').toLowerCase();
                        return name.includes(search) || position.includes(search);
                      })
                      .sort((a, b) => (a.user?.name || a.name || '').localeCompare(b.user?.name || b.name || ''))
                      .map(emp => (
                      <div key={emp.id} className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={scheduleForm.team_members.includes(emp.user_id || emp.id)}
                          onChange={(e) => {
                            const memberId = emp.user_id || emp.id;
                            if (e.target.checked) {
                              setScheduleForm({
                                ...scheduleForm,
                                team_members: [...scheduleForm.team_members, memberId]
                              });
                            } else {
                              setScheduleForm({
                                ...scheduleForm,
                                team_members: scheduleForm.team_members.filter(id => id !== memberId)
                              });
                            }
                          }}
                        />
                        <label className="flex-1 text-sm">
                          <span className="font-medium">{emp.user?.name || emp.name}</span>
                          {emp.position && <span className="text-gray-500 ml-1">- {emp.position}</span>}
                        </label>
                      </div>
                    ))}
                    {employees.filter(emp => {
                      const search = (scheduleForm.memberSearch || '').toLowerCase();
                      if (!search) return true;
                      const name = (emp.user?.name || emp.name || '').toLowerCase();
                      const position = (emp.position || '').toLowerCase();
                      return name.includes(search) || position.includes(search);
                    }).length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-2">No team members found</p>
                    )}
                  </div>
                  {scheduleForm.team_members.length > 0 && (
                    <p className="text-xs text-blue-600 mt-1">{scheduleForm.team_members.length} member(s) selected</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={scheduleForm.is_recurring}
                    onChange={(e) => setScheduleForm({...scheduleForm, is_recurring: e.target.checked})}
                  />
                  <Label>Make Recurring</Label>
                </div>

                {scheduleForm.is_recurring && (
                  <>
                    <div>
                      <Label>Recurring Pattern</Label>
                      <Select
                        value={scheduleForm.recurring_pattern}
                        onValueChange={(value) => setScheduleForm({...scheduleForm, recurring_pattern: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>End Date *</Label>
                      <Input
                        type="date"
                        value={scheduleForm.recurring_end_date}
                        onChange={(e) => setScheduleForm({...scheduleForm, recurring_end_date: e.target.value})}
                        required={scheduleForm.is_recurring}
                      />
                    </div>
                  </>
                )}

                {/* Conflict warnings with Time Split */}
                {Array.isArray(scheduleConflicts) && scheduleConflicts.length > 0 && (
                  <div className="space-y-3">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="font-semibold text-red-800 mb-3">Scheduling Conflicts Detected</p>
                      
                      {scheduleConflicts
                        .filter(c => c && typeof c === 'object' && c.user_name && !c.type && !c.loc)
                        .map((conflict, idx) => {
                          const times = splitTimes[conflict.user_id] || {};
                          return (
                            <div key={idx} className="mb-3 p-3 bg-white rounded border border-red-100">
                              <p className="font-medium text-red-700 mb-2">{conflict.user_name}</p>
                              
                              <div className="grid grid-cols-2 gap-3">
                                {/* Existing Project */}
                                <div className="p-2 bg-orange-50 rounded border border-orange-200">
                                  <p className="text-xs font-semibold text-orange-800 mb-1">{conflict.existing_project}</p>
                                  <p className="text-xs text-orange-600 mb-2">Currently: {conflict.existing_start_time || '07:00'} - {conflict.existing_end_time || '15:00'}</p>
                                  <div className="flex gap-1 items-center">
                                    <Input
                                      type="time"
                                      className="h-8 text-xs"
                                      value={times.existing_start || ''}
                                      onChange={(e) => setSplitTimes(prev => ({
                                        ...prev,
                                        [conflict.user_id]: { ...prev[conflict.user_id], existing_start: e.target.value }
                                      }))}
                                    />
                                    <span className="text-xs text-gray-400">to</span>
                                    <Input
                                      type="time"
                                      className="h-8 text-xs"
                                      value={times.existing_end || ''}
                                      onChange={(e) => setSplitTimes(prev => ({
                                        ...prev,
                                        [conflict.user_id]: { ...prev[conflict.user_id], existing_end: e.target.value }
                                      }))}
                                    />
                                  </div>
                                </div>
                                
                                {/* New Project */}
                                <div className="p-2 bg-blue-50 rounded border border-blue-200">
                                  <p className="text-xs font-semibold text-blue-800 mb-1">New Schedule</p>
                                  <p className="text-xs text-blue-600 mb-2">Requested: {scheduleForm.start_time} - {scheduleForm.end_time}</p>
                                  <div className="flex gap-1 items-center">
                                    <Input
                                      type="time"
                                      className="h-8 text-xs"
                                      value={times.new_start || ''}
                                      onChange={(e) => setSplitTimes(prev => ({
                                        ...prev,
                                        [conflict.user_id]: { ...prev[conflict.user_id], new_start: e.target.value }
                                      }))}
                                    />
                                    <span className="text-xs text-gray-400">to</span>
                                    <Input
                                      type="time"
                                      className="h-8 text-xs"
                                      value={times.new_end || ''}
                                      onChange={(e) => setSplitTimes(prev => ({
                                        ...prev,
                                        [conflict.user_id]: { ...prev[conflict.user_id], new_end: e.target.value }
                                      }))}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      }
                      
                      <p className="text-xs text-gray-500 mt-2">Adjust the times above to split the day between projects, then click "Apply Time Split".</p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => { setShowScheduleDialog(false); setScheduleConflicts([]); setSplitTimes({}); }}>
                    Cancel
                  </Button>
                  {scheduleConflicts.length > 0 && (
                    <>
                      <Button type="button" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => {
                        const conflictUserIds = scheduleConflicts.map(c => c.user_id);
                        const cleanMembers = scheduleForm.team_members.filter(id => !conflictUserIds.includes(id));
                        if (cleanMembers.length === 0) {
                          toast.error('No team members left after removing conflicts');
                          return;
                        }
                        setScheduleForm(prev => ({ ...prev, team_members: cleanMembers }));
                        setScheduleConflicts([]);
                        setSplitTimes({});
                        toast.info(`Removed ${conflictUserIds.length} conflicting member(s). Click "Create Schedule" to proceed.`);
                      }}>
                        Remove Conflicting
                      </Button>
                      <Button type="button" onClick={() => handleApplyTimeSplit(scheduleConflicts)} disabled={loading}>
                        {loading ? 'Applying...' : 'Apply Time Split & Create'}
                      </Button>
                    </>
                  )}
                  {scheduleConflicts.length === 0 && (
                    <Button type="submit" disabled={loading || scheduleForm.team_members.length === 0}>
                      Create Schedule
                    </Button>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Schedule Dialog */}
          <Dialog open={showEditScheduleDialog} onOpenChange={setShowEditScheduleDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Schedule</DialogTitle>
                <DialogDescription>
                  Update schedule details for {editingSchedule?.project_name}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateSchedule} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={scheduleForm.date}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Project</Label>
                    <ProjectCombobox
                      projects={projects}
                      value={scheduleForm.project_id}
                      onChange={(val) => setScheduleForm({ ...scheduleForm, project_id: val })}
                      filterActive={false}
                      testId="edit-schedule-project"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={scheduleForm.start_time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={scheduleForm.end_time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Status</Label>
                  <Select 
                    value={scheduleForm.status} 
                    onValueChange={(val) => setScheduleForm({ ...scheduleForm, status: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Team Members</Label>
                  <Input
                    type="text"
                    placeholder="Search team members..."
                    className="mb-2"
                    value={scheduleForm.editMemberSearch || ''}
                    onChange={(e) => setScheduleForm({...scheduleForm, editMemberSearch: e.target.value})}
                  />
                  <div className="border rounded p-2 max-h-40 overflow-y-auto">
                    {[...employees]
                      .filter(emp => {
                        const search = (scheduleForm.editMemberSearch || '').toLowerCase();
                        if (!search) return true;
                        const name = (emp.user?.name || emp.name || '').toLowerCase();
                        const position = (emp.position || '').toLowerCase();
                        return name.includes(search) || position.includes(search);
                      })
                      .sort((a, b) => (a.user?.name || a.name || '').localeCompare(b.user?.name || b.name || ''))
                      .map((emp) => (
                      <div key={emp.id} className="flex items-center gap-2 py-1">
                        <input
                          type="checkbox"
                          id={`edit-team-${emp.id}`}
                          checked={scheduleForm.team_members.includes(emp.user_id || emp.id)}
                          onChange={(e) => {
                            const memberId = emp.user_id || emp.id;
                            if (e.target.checked) {
                              setScheduleForm({
                                ...scheduleForm,
                                team_members: [...scheduleForm.team_members, memberId]
                              });
                            } else {
                              setScheduleForm({
                                ...scheduleForm,
                                team_members: scheduleForm.team_members.filter(id => id !== memberId)
                              });
                            }
                          }}
                        />
                        <label htmlFor={`edit-team-${emp.id}`} className="text-sm">
                          {emp.user?.name || emp.name} - {emp.position}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={scheduleForm.notes}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
                    placeholder="Add any notes about this schedule..."
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => {
                    setShowEditScheduleDialog(false);
                    setEditingSchedule(null);
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    Update Schedule
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Schedule Details Dialog */}
          <Dialog open={showScheduleDetailsDialog} onOpenChange={setShowScheduleDetailsDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Schedule Details
                </DialogTitle>
              </DialogHeader>
              {selectedScheduleDetails && (
                <div className="space-y-4">
                  {/* Project Info */}
                  <div className="border-b pb-3">
                    <h3 className="font-semibold text-lg">{selectedScheduleDetails.project_name}</h3>
                    {selectedScheduleDetails.project_address && (
                      <p className="text-sm text-gray-500">{selectedScheduleDetails.project_address}</p>
                    )}
                  </div>

                  {/* Date & Time */}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span>{formatDateDisplay(selectedScheduleDetails.date, { weekday: true })}</span>
                    </div>
                    {selectedScheduleDetails.start_time && selectedScheduleDetails.end_time && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span>{selectedScheduleDetails.start_time} - {selectedScheduleDetails.end_time}</span>
                      </div>
                    )}
                  </div>

                  {/* Team Members */}
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Team Members ({selectedScheduleDetails.team_details?.length || 0})
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                      {selectedScheduleDetails.team_details?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedScheduleDetails.team_details.map((member, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-2 bg-white rounded border">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-sm font-medium text-blue-700">
                                  {member.name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-sm">{member.name}</div>
                                {member.position && (
                                  <div className="text-xs text-gray-500">{member.position}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-2">No team members assigned</p>
                      )}
                    </div>
                  </div>

                  {/* Status & Recurring Info */}
                  <div className="flex items-center gap-3">
                    <Badge variant={
                      selectedScheduleDetails.status === 'completed' ? 'success' :
                      selectedScheduleDetails.status === 'in_progress' ? 'default' :
                      selectedScheduleDetails.status === 'cancelled' ? 'destructive' :
                      'secondary'
                    }>
                      {selectedScheduleDetails.status || 'scheduled'}
                    </Badge>
                    {selectedScheduleDetails.is_recurring && (
                      <Badge variant="outline">
                        Recurring: {selectedScheduleDetails.recurring_pattern}
                      </Badge>
                    )}
                  </div>

                  {/* Notes */}
                  {selectedScheduleDetails.notes && (
                    <div>
                      <h4 className="font-medium mb-1 text-sm">Notes</h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{selectedScheduleDetails.notes}</p>
                    </div>
                  )}

                  {/* Copy Schedule to Another Date */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <Copy className="w-4 h-4" />
                      Copy Schedule to Another Date
                    </h4>
                    <div className="flex gap-2">
                      <Input
                        type="date"
                        value={copyTargetDate}
                        onChange={(e) => setCopyTargetDate(e.target.value)}
                        className="flex-1"
                        data-testid="copy-schedule-date"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleCopySchedule(selectedScheduleDetails, copyTargetDate)}
                        disabled={!copyTargetDate || copyingSchedule}
                        data-testid="copy-schedule-btn"
                      >
                        {copyingSchedule ? 'Copying...' : 'Copy'}
                      </Button>
                    </div>

                    {/* Copy Conflict Resolution */}
                    {copyConflictData && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                        <p className="font-medium text-sm text-red-800 mb-2">Scheduling Conflicts on {formatDateDisplay(copyConflictData.targetDate)}</p>
                        {copyConflictData.conflicts.map((c, idx) => {
                          const times = splitTimes[c.user_id] || {};
                          if (!times.existing_start) {
                            // Initialize split times for copy conflicts
                            const existStart = c.existing_start_time || '07:00';
                            const existEnd = c.existing_end_time || '15:00';
                            const copyStart = selectedScheduleDetails?.start_time || '07:00';
                            const copyEnd = selectedScheduleDetails?.end_time || '15:00';
                            const midHour = Math.floor((parseInt(existStart) + parseInt(existEnd)) / 2);
                            const midTime = `${String(midHour).padStart(2, '0')}:00`;
                            setSplitTimes(prev => ({
                              ...prev,
                              [c.user_id]: { existing_start: existStart, existing_end: midTime, new_start: midTime, new_end: existEnd }
                            }));
                          }
                          return (
                            <div key={idx} className="mb-2 p-2 bg-white rounded border border-red-100">
                              <p className="font-medium text-sm mb-2">{c.user_name}</p>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 bg-orange-50 rounded border border-orange-200">
                                  <p className="text-xs font-semibold text-orange-800">{c.existing_project}</p>
                                  <p className="text-xs text-orange-600 mb-1">Currently: {c.existing_start_time} - {c.existing_end_time}</p>
                                  <div className="flex gap-1 items-center">
                                    <Input type="time" className="h-7 text-xs" value={times.existing_start || ''} onChange={(e) => setSplitTimes(prev => ({ ...prev, [c.user_id]: { ...prev[c.user_id], existing_start: e.target.value } }))} />
                                    <span className="text-xs">-</span>
                                    <Input type="time" className="h-7 text-xs" value={times.existing_end || ''} onChange={(e) => setSplitTimes(prev => ({ ...prev, [c.user_id]: { ...prev[c.user_id], existing_end: e.target.value } }))} />
                                  </div>
                                </div>
                                <div className="p-2 bg-blue-50 rounded border border-blue-200">
                                  <p className="text-xs font-semibold text-blue-800">New Schedule</p>
                                  <p className="text-xs text-blue-600 mb-1">Copy from: {selectedScheduleDetails?.start_time} - {selectedScheduleDetails?.end_time}</p>
                                  <div className="flex gap-1 items-center">
                                    <Input type="time" className="h-7 text-xs" value={times.new_start || ''} onChange={(e) => setSplitTimes(prev => ({ ...prev, [c.user_id]: { ...prev[c.user_id], new_start: e.target.value } }))} />
                                    <span className="text-xs">-</span>
                                    <Input type="time" className="h-7 text-xs" value={times.new_end || ''} onChange={(e) => setSplitTimes(prev => ({ ...prev, [c.user_id]: { ...prev[c.user_id], new_end: e.target.value } }))} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => { setCopyConflictData(null); setSplitTimes({}); }}>
                            Cancel
                          </Button>
                          <Button size="sm" variant="outline" className="border-orange-300 text-orange-700"
                            onClick={() => {
                              const conflictIds = copyConflictData.conflicts.map(c => c.user_id);
                              const cleanMembers = (copyConflictData.schedule.team_members || []).filter(id => !conflictIds.includes(id));
                              if (cleanMembers.length === 0) { toast.error('No members left'); return; }
                              handleCopySchedule({ ...copyConflictData.schedule, team_members: cleanMembers }, copyConflictData.targetDate, true);
                              setCopyConflictData(null); setSplitTimes({});
                            }}>
                            Copy Without Conflicting
                          </Button>
                          <Button size="sm" onClick={() => handleApplyTimeSplit(copyConflictData.conflicts, true)} disabled={loading}>
                            {loading ? 'Applying...' : 'Apply Time Split'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-3 border-t">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        setShowScheduleDetailsDialog(false);
                        handleEditSchedule(selectedScheduleDetails);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Schedule
                    </Button>
                    <Button 
                      variant="outline" 
                      className="text-red-600 hover:text-red-700"
                      onClick={() => {
                        setShowScheduleDetailsDialog(false);
                        handleDeleteSchedule(selectedScheduleDetails.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <MobileScheduleBuilder
            open={showBuilder}
            onOpenChange={setShowBuilder}
            projects={projects}
            employees={employees}
            onSaved={fetchSchedules}
          />
    </>
  );
}

export default ScheduleManagement;
